import type { Dispatch, SetStateAction } from 'react';
import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Grid,
  Typography,
  Button,
} from '@mui/material';
import {
  SubscriptionTier,
  type OrganizationOnAzure,
} from '../../../../generated-client/models';
import { isThereAnActiveTrial } from '../../../utils/organization_details_helper/organization_details_helper';
import { convertTimestampToFullUTCDate } from '../../../utils/date_convert';
import { getTierLabelKey } from '../../../utils/entitlements/tierLabels';

type DetailsProps = {
  organization: OrganizationOnAzure;
  setShowEditOrganizationModal: Dispatch<SetStateAction<boolean>>;
};

const Details = ({
  organization,
  setShowEditOrganizationModal,
}: DetailsProps) => {
  const opsLabel = getTierLabelKey(SubscriptionTier.Ops);
  const respondLabel = getTierLabelKey(SubscriptionTier.Respond);

  return (
    <>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Typography variant="h4" component="h2" style={{ marginTop: '20px' }}>
            {organization.company_name}
          </Typography>
        </Grid>
        <Grid
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Button
            variant="contained"
            style={{
              borderRadius: '10px',
              height: '42px',
              paddingBottom: 3,
              textAlign: 'center',
              marginTop: '20px',
            }}
            onClick={() => {
              setShowEditOrganizationModal(true);
            }}
          >
            Edit {organization.company_name} Subscription
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: '20px' }}>
        <Grid size={12}>
          <TableContainer sx={{ boxShadow: 0 }} component={Paper}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>
                    Company Code
                  </TableCell>
                  <TableCell>{organization.org_code}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>Address</TableCell>
                  <TableCell>{organization.address}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>
                    Primary Contact
                  </TableCell>
                  <TableCell>{organization.primary_contact}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>Phone</TableCell>
                  <TableCell>{organization.phone_number}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell>{organization.email || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>Join Pin</TableCell>
                  <TableCell>{organization.join_org_pin ?? 'Disabled'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>
                    Safeguard Organization
                  </TableCell>
                  <TableCell>
                    {organization.is_safeguard_organization ? 'Yes' : 'No'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>
                    {opsLabel} Subscription Count
                  </TableCell>
                  <TableCell>{organization.ops_subscription_count}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>
                    {respondLabel} Subscription Count
                  </TableCell>
                  <TableCell>{organization.respond_subscription_count}</TableCell>
                </TableRow>
                {organization.trial_end_timestamp && (
                  <>
                    <TableRow>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Trial Is Active
                      </TableCell>
                      <TableCell>
                        {isThereAnActiveTrial(organization) ? 'Yes' : 'No'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Trial End Date
                      </TableCell>
                      <TableCell>
                        {convertTimestampToFullUTCDate(
                          organization.trial_end_timestamp,
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default Details;
